import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { PlusIcon, MinusIcon, TrashIcon, CheckCircleIcon, ClockIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';
import type { CakeDetails, CartItem } from '../context/CartContext';
import SquarePaymentForm from '../components/SquarePaymentForm';
import CakeDetailsForm from '../components/CakeDetailsForm';
import HelpfulGuides from '../components/HelpfulGuides';
import UberEatsOrderLink from '../components/UberEatsOrderLink';
import StuffedCookieSelectionModal from '../components/StuffedCookieSelectionModal';
import ButterTartSelectionModal from '../components/ButterTartSelectionModal';
import { getTotalCookiesFromName } from '../utils/cookiePieces';
import { formatCalendarDateEnCA, localYyyyMmDd } from '../utils/calendarDate';
import { capPickupDates, earliestYmd, isOrderByClosed } from '../utils/seasonalPreorder';
import {
  publicActionErrorMessage,
  publicCatchMessage,
  publicGuardUserMessage,
  publicLoadErrorMessage,
  publicMessageFromApiErrorResponse,
} from '../lib/publicSiteErrorMessage';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';

// API URL - use environment variable or fallback to relative path (for production)
const API_URL = import.meta.env.VITE_API_URL || '';

/** After embedded pay fails, offer Square-hosted checkout unless the message is a known non-retry case. */
function shouldOfferSquareHostedCheckout(message: string | null): boolean {
  const m = (message || '').toLowerCase();
  if (!m) return false;
  if (m.includes('already paid')) return false;
  if (m.includes('sold out')) return false;
  if (m.includes('does not match')) return false;
  if (m.includes('mismatch')) return false;
  // Hosted checkout creates its own Square order — best fallback when embedded link failed.
  if (m.includes('still connecting to square')) return true;
  if (m.includes('not configured')) return false;
  return true;
}

// Checkout steps
type CheckoutStep = 'cart' | 'payment' | 'confirmation';

export default function Order() {
  const navigate = useNavigate();
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.order,
    og: {
      type: 'website',
      title: PAGE_SEO.order.title,
      description: PAGE_SEO.order.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const {
    items: cartItems,
    addItem,
    removeItem,
    updateQuantity,
    updateItemNotes,
    totalPrice, 
    clearCart, 
    isCartOpen, 
    setCartOpen,
    cakeDetails,
    setCakeDetails,
    hasCakeItems,
  } = useCart();
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', pickupDate: '', pickupTime: '', notes: '' });
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('cart');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  /** Authoritative total from POST /api/orders — used for Square so client tax rounding cannot mismatch DB. */
  const [paymentTotal, setPaymentTotal] = useState<number | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [stuffedCookieEditItem, setStuffedCookieEditItem] = useState<CartItem | null>(null);
  const [butterTartEditItem, setButterTartEditItem] = useState<CartItem | null>(null);
  const [bakeryPhone, setBakeryPhone] = useState<string | null>(null);
  const [businessHours, setBusinessHours] = useState<Array<{ day: string; open: string; close: string; isClosed: boolean }>>([]);
  const [closureDates, setClosureDates] = useState<Set<string>>(new Set());
  /** Open pickup dates from portal hours — customers can only pick from this list. */
  const [availablePickupDates, setAvailablePickupDates] = useState<string[]>([]);
  /** Menu rows that close ordering or pickup early (Thanksgiving pies). */
  const [preorderById, setPreorderById] = useState<Record<string, { order_by?: string | null; pickup_through?: string | null }>>({});
  /** When set, POST /api/orders includes campaign fields for fundraiser reporting. */
  const [campaignCheckout, setCampaignCheckout] = useState<{ id: string; slug: string } | null>(null);
  const [campaignBootstrapError, setCampaignBootstrapError] = useState<string | null>(null);
  const campaignBootstrapSlugRef = useRef<string | null>(null);
  // Resume payment from abandoned-cart email link (?pay=token)
  const [searchParams] = useSearchParams();
  const payToken = searchParams.get('pay');
  const [resumeOrder, setResumeOrder] = useState<{
    id: string;
    order_number: string;
    total: number;
    subtotal: number;
    tax: number;
    items: Array<{ name: string; quantity: number; price: number }>;
    customer_name: string;
    pickup_date: string | null;
    pickup_time: string | null;
  } | null>(null);
  const [resumeLoading, setResumeLoading] = useState(!!payToken);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [hostedCheckoutLoading, setHostedCheckoutLoading] = useState(false);
  const [hostedReturnWorking, setHostedReturnWorking] = useState(false);
  const [hostedReturnError, setHostedReturnError] = useState<string | null>(null);

  // Fetch order by resume token when ?pay= is present (abandoned-cart link)
  useEffect(() => {
    if (!payToken) return;
    const url = API_URL ? `${API_URL}/api/orders/resume/${payToken}` : `/api/orders/resume/${payToken}`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          // Do not surface raw API or proxy errors to customers on resume links.
          if (res.status === 404 || res.status === 410) {
            setResumeError('This link has expired or the order was already paid.');
            return null;
          }
          let body: unknown = null;
          try {
            body = await res.json();
          } catch {
            /* ignore */
          }
          setResumeError(publicMessageFromApiErrorResponse(res.status, body));
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) setResumeOrder(data);
      })
      .catch(() => setResumeError('Unable to load order. Please try again.'))
      .finally(() => setResumeLoading(false));
  }, [payToken]);

  const hostedDone = searchParams.get('hosted_done');
  const hostedOrderIdFromUrl = searchParams.get('order_id');

  // After Square hosted checkout redirect: poll until the order shows paid, then confirm like embedded pay.
  useEffect(() => {
    if (hostedDone !== '1' || !hostedOrderIdFromUrl) return;
    let cancelled = false;
    setHostedReturnWorking(true);
    setHostedReturnError(null);

    const pollOnce = async () => {
      const path = `/api/payments/order-payment-status/${encodeURIComponent(hostedOrderIdFromUrl)}`;
      const res = await fetch(API_URL ? `${API_URL}${path}` : path);
      const data = (await res.json().catch(() => ({}))) as { paid?: boolean; order_number?: string };
      return data;
    };

    (async () => {
      try {
        for (let i = 0; i < 18 && !cancelled; i++) {
          const data = await pollOnce();
          if (data.paid && data.order_number) {
            setOrderNumber(data.order_number);
            setOrderId(hostedOrderIdFromUrl);
            setCheckoutStep('confirmation');
            clearCart();
            setCartOpen(true);
            setPaymentError(null);
            navigate('/order', { replace: true });
            setHostedReturnWorking(false);
            return;
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
        if (!cancelled) {
          setHostedReturnError(
            'We could not confirm your payment yet. If you completed checkout on Square, wait a minute and refresh. You can also call the bakery.',
          );
        }
      } catch {
        if (!cancelled) setHostedReturnError(publicLoadErrorMessage());
      } finally {
        if (!cancelled) setHostedReturnWorking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hostedDone, hostedOrderIdFromUrl, navigate, clearCart, setCartOpen, API_URL]);

  // Fundraiser / campaign checkout: ?campaign=slug preloads the campaign SKU (one fresh cart per visitor).
  const campaignSlugParam = searchParams.get('campaign');
  useEffect(() => {
    if (payToken) return;
    if (!campaignSlugParam) {
      campaignBootstrapSlugRef.current = null;
      setCampaignCheckout(null);
      setCampaignBootstrapError(null);
      return;
    }
    if (campaignBootstrapSlugRef.current === campaignSlugParam) return;

    let cancelled = false;
    setCampaignBootstrapError(null);

    const url = API_URL
      ? `${API_URL}/api/campaigns/public/${encodeURIComponent(campaignSlugParam)}`
      : `/api/campaigns/public/${encodeURIComponent(campaignSlugParam)}`;

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('This fundraiser link is not available right now.');
          }
          let body: unknown = null;
          try {
            body = await res.json();
          } catch {
            /* ignore */
          }
          throw new Error(publicMessageFromApiErrorResponse(res.status, body));
        }
        return res.json() as Promise<{
          checkout_allowed?: boolean;
          campaign: {
            id: string;
            slug: string;
            sold_out: boolean;
            menu_item_eligible: boolean;
            menu_item_block_reason: string | null;
          };
          menu_item: {
            id: string;
            name: string;
            price: number;
            category: string;
            tax_applies?: boolean | null;
            image_url: string | null;
          } | null;
        }>;
      })
      .then((data) => {
        if (cancelled || !data?.campaign) return;
        const allow =
          data.checkout_allowed !== undefined
            ? data.checkout_allowed
            : !data.campaign.sold_out && data.campaign.menu_item_eligible;
        if (!allow) {
          throw new Error(
            data.campaign.menu_item_block_reason ||
              'This fundraiser link cannot be used for checkout right now. Use the full menu instead.'
          );
        }
        if (!data.menu_item) {
          throw new Error('This fundraiser is not set up correctly yet. Please use the regular menu.');
        }
        clearCart();
        const mi = data.menu_item;
        addItem({
          id: mi.id,
          name: mi.name,
          price: mi.price,
          image: mi.image_url || getImage('placeholder_menu', '/assets/images/placeholder.webp'),
          category: mi.category,
          tax_applies: mi.tax_applies ?? undefined,
        });
        setCampaignCheckout({ id: data.campaign.id, slug: data.campaign.slug });
        campaignBootstrapSlugRef.current = campaignSlugParam;
      })
      .catch((e) => {
        if (!cancelled) {
          setCampaignBootstrapError(publicCatchMessage(e));
          setCampaignCheckout(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [payToken, campaignSlugParam, addItem, clearCart, getImage]);

  // Fetch bakery phone for payment-failure fallback (call to confirm order)
  useEffect(() => {
    const url = API_URL ? `${API_URL}/api/settings/contact` : '/api/settings/contact';
    fetch(url)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (data?.phone) setBakeryPhone(data.phone); })
      .catch(() => {});
  }, []);

  // Fetch business hours so we only allow pickup on open days and within open times
  useEffect(() => {
    const url = API_URL ? `${API_URL}/api/settings/hours` : '/api/settings/hours';
    fetch(url)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (data?.hours?.length) setBusinessHours(data.hours); })
      .catch(() => {});
  }, []);

  // Fetch holiday/one-off closure dates so we show them as closed (no pickup time)
  useEffect(() => {
    const url = API_URL ? `${API_URL}/api/settings/closures` : '/api/settings/closures';
    fetch(url)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        const dates = (data?.closures ?? []).map((c: { closure_date: string }) => c.closure_date);
        setClosureDates(new Set(dates));
      })
      .catch(() => {});
  }, []);

  // Fetch open pickup dates from portal settings (dynamic — follows Tammy's hours + closures)
  useEffect(() => {
    const url = API_URL ? `${API_URL}/api/settings/pickup-dates?days=90` : '/api/settings/pickup-dates?days=90';
    fetch(url)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (Array.isArray(data?.dates)) setAvailablePickupDates(data.dates);
      })
      .catch(() => {});
  }, []);

  // Seasonal items (order_by / pickup_through) cap the pickup list. Regular items are unchanged.
  useEffect(() => {
    const url = API_URL ? `${API_URL}/api/menu?orderable=true` : '/api/menu?orderable=true';
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const map: Record<string, { order_by?: string | null; pickup_through?: string | null }> = {};
        for (const item of data?.items || []) {
          if (item?.id && (item.order_by || item.pickup_through)) {
            map[item.id] = { order_by: item.order_by, pickup_through: item.pickup_through };
          }
        }
        setPreorderById(map);
      })
      .catch(() => {});
  }, []);

  // Business hours: 0=Monday .. 6=Sunday (same as backend)
  const getDayIndexForDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    const jsDay = d.getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  };

  const getDayInfoForDate = (dateStr: string) =>
    businessHours[getDayIndexForDate(dateStr)];

  const isClosureDate = (dateStr: string) =>
    closureDates.has(dateStr);

  // Generate pickup time options (e.g. "11:00 AM") in 30-min steps between open and close
  const getPickupTimeOptions = (dateStr: string): string[] => {
    if (isClosureDate(dateStr)) return [];
    const dayInfo = getDayInfoForDate(dateStr);
    if (!dayInfo?.open || !dayInfo?.close || dayInfo.isClosed) return [];
    const [openH, openM] = dayInfo.open.split(':').map(Number);
    const [closeH, closeM] = dayInfo.close.split(':').map(Number);
    const openMin = openH * 60 + (openM || 0);
    const closeMin = closeH * 60 + (closeM || 0);
    const options: string[] = [];
    for (let m = openMin; m < closeMin; m += 30) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      options.push(`${h12}:${String(min).padStart(2, '0')} ${ampm}`);
    }
    return options;
  };

  const isPickupDayClosed = customerInfo.pickupDate
    ? (isClosureDate(customerInfo.pickupDate) || getDayInfoForDate(customerInfo.pickupDate)?.isClosed === true)
    : false;
  const pickupTimeOptions = customerInfo.pickupDate ? getPickupTimeOptions(customerInfo.pickupDate) : [];

  const pickupCap = useMemo(() => earliestYmd(
    cartItems.map((item) => preorderById[item.id]?.pickup_through)
  ), [cartItems, preorderById]);

  const closedPreorderNames = useMemo(() => {
    const today = localYyyyMmDd();
    return cartItems
      .filter((item) => isOrderByClosed(preorderById[item.id]?.order_by, today))
      .map((item) => item.name);
  }, [cartItems, preorderById]);

  const visiblePickupDates = useMemo(
    () => capPickupDates(availablePickupDates, pickupCap),
    [availablePickupDates, pickupCap]
  );

  const isStuffedCookie = (item: CartItem) =>
    item.category === 'Stuffed Cookies' || item.name.toLowerCase().includes('stuffed');

  const isButterTart = (item: CartItem) =>
    item.category === 'Tarts' && item.name.toLowerCase().includes('butter tart');

  // Tax rules per CRA (Ontario): 6+ single-serving baked goods = zero-rated; sandwiches always taxable; cakes exempt.
  const getPiecesForItem = (item: CartItem) =>
    isStuffedCookie(item) ? getTotalCookiesFromName(item.name) * item.quantity : item.quantity;
  const isBakedGoodForSixRule = (item: { category?: string }) =>
    !(item.category ?? '').toLowerCase().includes('sandwiches & savory');
  const isCake = (item: { category?: string }) =>
    (item.category ?? '').toLowerCase().includes('cake');
  const totalBakedPieces = cartItems
    .filter(isBakedGoodForSixRule)
    .reduce((sum, i) => sum + getPiecesForItem(i), 0);
  const bakedGoodsZeroRated = totalBakedPieces >= 6;
  const isItemTaxableByCategory = (i: typeof cartItems[0]) => {
    if (isCake(i)) return false;
    if (!isBakedGoodForSixRule(i)) return true;
    return !bakedGoodsZeroRated;
  };
  const taxableSubtotal = cartItems
    .filter((i) => {
      if (i.tax_applies === false) return false;
      if (i.tax_applies === true) return true;
      return isItemTaxableByCategory(i);
    })
    .reduce((sum, i) => sum + i.price * i.quantity, 0);
  // Match backend orderTax.js rounding so cart display aligns with charged total.
  const tax = Math.round(taxableSubtotal * 0.13 * 100) / 100;
  const subtotal = totalPrice;
  const total = Math.round((subtotal + taxableSubtotal * 0.13) * 100) / 100;
  const taxLabel = tax > 0 ? 'HST (13%):' : 'HST:';
  const taxNote = tax === 0 ? ' (6+ baked items or cakes exempt)' : '';
  const taxRuleNote = 'HST 13% on taxable items only. 6+ baked items or cakes exempt. Sandwiches always taxable.';
  const sixPlusMessage =
    tax > 0 && totalBakedPieces < 6
      ? `6+ baked items = no HST on those. Add ${6 - totalBakedPieces} more baked item(s) (e.g. cookie, tart) to qualify.`
      : tax === 0
        ? 'You qualify for no HST on this order.'
        : null;

  // Handler for cake details form changes
  const handleCakeDetailsChange = useCallback((details: CakeDetails) => {
    setCakeDetails(details);
  }, [setCakeDetails]);


  // Create order in backend
  const createOrder = async () => {
    const ordersUrl = API_URL ? `${API_URL}/api/orders` : '/api/orders';
    const response = await fetch(ordersUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          requiresApproval: item.requiresApproval,
          notes: item.notes,
          customizations: item.customizations,
          category: item.category,
          tax_applies: item.tax_applies ?? undefined,
        })),
        pickup_date: customerInfo.pickupDate,
        pickup_time: customerInfo.pickupTime,
        special_instructions: customerInfo.notes,
        ...(campaignCheckout
          ? { campaign_id: campaignCheckout.id, campaign_slug: campaignCheckout.slug }
          : {}),
        // Cake-specific fields
        requires_approval: hasCakeItems,
        cake_details: hasCakeItems ? cakeDetails : null,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = errorText ? JSON.parse(errorText) : null;
      } catch {
        parsed = null;
      }
      throw new Error(publicMessageFromApiErrorResponse(response.status, parsed));
    }

    try {
      return await response.json();
    } catch {
      console.error('Order create: success response was not valid JSON');
      throw new Error(publicActionErrorMessage());
    }
  };

  const handleCheckout = async () => {
    // Clear previous errors
    setValidationError(null);
    setPaymentError(null);

    // Validate cart
    if (cartItems.length === 0) {
      setValidationError('Your cart is empty! Please add items before checking out.');
      return;
    }

    // Validate required fields
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.email || !customerInfo.pickupDate) {
      setValidationError('Please fill in your name, phone, email, and pickup date.');
      return;
    }

    // Pickup must be on an open day; time (if set) must be within hours.
    if (isPickupDayClosed) {
      const dayInfo = getDayInfoForDate(customerInfo.pickupDate);
      setValidationError(
        isClosureDate(customerInfo.pickupDate)
          ? "We're closed on this date (holiday). Please choose another pickup date when we're open — you can still order now."
          : `We're closed on ${dayInfo?.day ?? 'that day'}. Please choose another pickup date when we're open — you can still order now.`
      );
      return;
    }
    if (customerInfo.pickupTime && pickupTimeOptions.length > 0 && !pickupTimeOptions.includes(customerInfo.pickupTime)) {
      setValidationError('Please select a pickup time during our open hours for that day.');
      return;
    }
    if (closedPreorderNames.length > 0) {
      setValidationError(`Orders have closed for ${closedPreorderNames.join(', ')}.`);
      return;
    }
    if (pickupCap && customerInfo.pickupDate > pickupCap) {
      setValidationError(`Pickup for this order must be on or before ${pickupCap}, on a day we're open.`);
      return;
    }

    // Validate cake details if ordering cake
    if (hasCakeItems) {
      if (!cakeDetails?.size || !cakeDetails?.flavor) {
        setValidationError('Please select cake size and flavor before checking out.');
        return;
      }
    }

    setIsCheckingOut(true);

    try {
      // Create the order first
      const order = await createOrder();
      setOrderId(order.id);
      setOrderNumber(order.order_number);
      const serverTotal = Number(order.total);
      setPaymentTotal(Number.isFinite(serverTotal) ? serverTotal : total);

      // Cake orders skip payment - go straight to confirmation (awaiting approval)
      if (hasCakeItems) {
        setCheckoutStep('confirmation');
        clearCart();
        return;
      }

      // All other orders: online payment only (no Pay at Pickup selector)
      setCheckoutStep('payment');
    } catch (error) {
      console.error('Checkout / create order:', error);
      setValidationError(publicCatchMessage(error));
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handlePaymentSuccess = (_paymentId: string, _receiptUrl?: string) => {
    if (resumeOrder) {
      setOrderNumber(resumeOrder.order_number);
      setCustomerInfo((prev) => ({
        ...prev,
        pickupDate: resumeOrder.pickup_date || '',
        pickupTime: resumeOrder.pickup_time || '',
      }));
    }
    setCheckoutStep('confirmation');
    clearCart();
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(publicGuardUserMessage(error));
  };

  const startHostedCheckout = useCallback(
    async (bakeryOrderId: string) => {
      setHostedCheckoutLoading(true);
      try {
        const url = API_URL
          ? `${API_URL}/api/payments/hosted-checkout-for-order`
          : '/api/payments/hosted-checkout-for-order';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: bakeryOrderId }),
        });
        let data: Record<string, unknown> = {};
        try {
          data = (await res.json()) as Record<string, unknown>;
        } catch {
          data = {};
        }
        if (!res.ok) {
          throw new Error(publicMessageFromApiErrorResponse(res.status, data));
        }
        const checkoutUrl = typeof data.checkoutUrl === 'string' ? data.checkoutUrl : '';
        if (!checkoutUrl) throw new Error(publicActionErrorMessage());
        window.location.href = checkoutUrl;
      } catch (e) {
        setPaymentError(publicCatchMessage(e));
        setHostedCheckoutLoading(false);
      }
    },
    [API_URL],
  );

  const handleCloseCart = () => {
    if (checkoutStep === 'confirmation') {
      // Reset everything after viewing confirmation
      setCartOpen(false);
      setCheckoutStep('cart');
      setOrderId(null);
      setOrderNumber(null);
      setCustomerInfo({ name: '', phone: '', email: '', pickupDate: '', pickupTime: '', notes: '' });
      setValidationError(null);
      setPaymentError(null);
    } else if (checkoutStep === 'payment') {
      // Go back to cart from payment
      setCheckoutStep('cart');
    } else {
      setCartOpen(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-bakery-cream to-white min-h-screen">
      {/* Header */}
      <section className="gradient-primary text-white section-padding">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-display font-bold mb-4"
          >
            Order Online
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-100 max-w-2xl mx-auto"
          >
            This page is for <strong>pickup orders</strong> at our Barrie bakery. Want <strong>delivery</strong>? Use Uber Eats below.
          </motion.p>
        </div>
      </section>

      {/* Delivery vs pickup: Uber Eats opens in a new tab; checkout here stays in-store pickup */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container-custom max-w-4xl py-4 px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">Delivery:</span>{' '}
            Order through Uber Eats — menu and fees are on their site.
          </p>
          <UberEatsOrderLink variant="banner" className="shrink-0 self-start sm:self-auto" />
        </div>
      </div>

      {(hostedReturnWorking || hostedReturnError) && (
        <div className="bg-white border-b border-violet-100">
          <div className="container-custom max-w-4xl py-3 px-4 text-sm">
            {hostedReturnWorking && (
              <p className="text-violet-900">Confirming your payment… This usually takes a few seconds.</p>
            )}
            {hostedReturnError && <p className="text-amber-900">{hostedReturnError}</p>}
          </div>
        </div>
      )}

      {/* Cart & Checkout Section */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          {campaignCheckout && (
            <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              <strong>Fundraiser checkout:</strong> the campaign item is in your cart. You can add more from the menu before you pay.
            </div>
          )}
          {campaignBootstrapError && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {campaignBootstrapError}{' '}
              <Link to="/menu" className="font-medium underline">
                Browse menu
              </Link>
            </div>
          )}
          {/* Resume payment from abandoned-cart email (?pay=token) */}
          {payToken && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              {resumeLoading && (
                <p className="text-gray-600 text-center py-8">Loading your order...</p>
              )}
              {resumeError && !resumeLoading && (
                <div className="text-center py-8">
                  <p className="text-gray-700 mb-4">{resumeError}</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/order" className="btn-primary">Order page</Link>
                    <Link to="/menu" className="btn-outline">Browse menu</Link>
                  </div>
                </div>
              )}
              {resumeOrder && checkoutStep === 'confirmation' && (
                <div className="text-center py-8">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 mb-4">
                    Your order <span className="font-semibold">#{resumeOrder.order_number}</span> has been placed.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 text-left mb-6 max-w-sm mx-auto">
                    <p className="text-sm text-gray-600">
                      <strong>Pickup:</strong>{' '}
                      {resumeOrder.pickup_date
                        ? `${resumeOrder.pickup_date}${resumeOrder.pickup_time ? ` at ${resumeOrder.pickup_time}` : ''}`
                        : 'To be confirmed — we will follow up with you'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Payment:</strong> Paid online
                    </p>
                  </div>
                  <Link to="/menu" className="btn-primary">Continue shopping</Link>
                </div>
              )}
              {resumeOrder && checkoutStep !== 'confirmation' && (
                <>
                  <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">Complete your payment</h2>
                  <p className="text-gray-600 mb-4">Order #{resumeOrder.order_number}</p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <ul className="space-y-1 text-sm text-gray-700">
                      {resumeOrder.items.map((item: { name: string; quantity: number; price: number }, i: number) => (
                        <li key={i}>
                          {item.name} {item.quantity > 1 ? `× ${item.quantity}` : ''} — ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 pt-3 border-t flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-primary-600">${resumeOrder.total.toFixed(2)}</span>
                    </div>
                  </div>
                  {paymentError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{paymentError}</p>
                      {bakeryPhone && (
                        <p className="text-gray-700 text-sm mt-2">
                          Having trouble? Call us: <a href={`tel:${bakeryPhone.replace(/\D/g, '')}`} className="font-medium text-primary-600 hover:underline">{bakeryPhone}</a>
                        </p>
                      )}
                    </div>
                  )}
                  <SquarePaymentForm
                    amount={resumeOrder.total}
                    orderId={resumeOrder.id}
                    customerEmail={undefined}
                    customerName={resumeOrder.customer_name}
                    campaignId={campaignCheckout?.id}
                    campaignSlug={campaignCheckout?.slug}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    onCancel={() => window.location.href = '/order'}
                  />
                  {paymentError && shouldOfferSquareHostedCheckout(paymentError) && (
                    <div className="mt-4">
                      <button
                        type="button"
                        disabled={hostedCheckoutLoading}
                        onClick={() => startHostedCheckout(resumeOrder.id)}
                        className="w-full text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 rounded-lg px-4 py-2 disabled:opacity-60"
                      >
                        {hostedCheckoutLoading
                          ? 'Opening secure Square checkout…'
                          : 'Continue on Square’s secure checkout page'}
                      </button>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Use this if the card form fails (browser extensions or bank verification).
                      </p>
                    </div>
                  )}
                  <p className="text-center mt-4">
                    <Link to="/menu" className="text-primary-600 hover:underline text-sm">Back to menu</Link>
                  </p>
                </>
              )}
            </div>
          )}

          {/* Normal flow: Empty Cart State - Show when cart is empty and not in checkout flow */}
          {!payToken && cartItems.length === 0 && checkoutStep === 'cart' && (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <ShoppingCartIcon className="h-20 w-20 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-3">
                Your cart is empty
              </h2>
              <p className="text-gray-600 mb-6">
                Browse our menu and add items to your cart to get started!
              </p>
              <Link
                to="/menu"
                className="btn-primary inline-flex items-center gap-2"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                Browse Menu
              </Link>
            </div>
          )}

          {/* Cart with Items - Show cart summary when items exist (not in resume-payment flow) */}
          {!payToken && cartItems.length > 0 && checkoutStep === 'cart' && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                  Review Your Order
                </h2>
                <p className="text-gray-600 mb-4">
                  {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your cart
                </p>
                <Link
                  to="/menu"
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                >
                  <ShoppingCartIcon className="h-4 w-4" />
                  Browse Menu to Add More Items
                </Link>
              </div>

              {/* Cart Items List */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.lineId} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      {item.notes && (
                        <p className="text-xs text-gray-600 mt-1">{item.notes}</p>
                      )}
                      {isStuffedCookie(item) && (
                        <button
                          type="button"
                          onClick={() => setStuffedCookieEditItem(item)}
                          className="text-xs text-primary-600 hover:underline mt-1"
                          data-testid="change-flavours"
                        >
                          Change flavours
                        </button>
                      )}
                      {isButterTart(item) && (
                        <button
                          type="button"
                          onClick={() => setButterTartEditItem(item)}
                          className="text-xs text-primary-600 hover:underline mt-1"
                          data-testid="change-tart-flavour"
                        >
                          Change flavour
                        </button>
                      )}
                      <p className="text-sm text-gray-600">
                        ${item.price.toFixed(2)} each
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                        aria-label="Decrease quantity"
                        title="Decrease quantity"
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                        aria-label="Increase quantity"
                        title="Increase quantity"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeItem(item.lineId)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded ml-2"
                        aria-label="Remove item"
                        title="Remove item"
                        data-testid="remove-cart-item"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Total */}
              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between text-sm mb-1">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between text-sm mb-1 text-gray-600">
                    <span>Taxable amount (before HST):</span>
                    <span>${taxableSubtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mb-2">
                  <span>{taxLabel}{taxNote}</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{taxRuleNote}</p>
                {sixPlusMessage && (
                  <p className="text-xs text-primary-600 mb-2">{sixPlusMessage}</p>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span className="text-primary-600">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Validation Error Message */}
              {validationError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{validationError}</p>
                </div>
              )}

              {/* Continue Shopping Link */}
              <div className="text-center mb-4">
                <Link
                  to="/menu"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  ← Continue Shopping
                </Link>
              </div>

              {/* Checkout Button - Opens cart sidebar */}
              <button
                onClick={() => setCartOpen(true)}
                className="btn-primary w-full text-lg py-4"
              >
                Proceed to Checkout
              </button>
            </div>
          )}

          {/* Custom Cakes Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center mt-8">
            <h3 className="text-xl font-display font-bold text-amber-800 mb-2">
              🎂 Custom Cakes
            </h3>
            <p className="text-amber-700 mb-4">
              Custom cakes require 72 hours notice. Please call or message us to order.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:7055034966" className="btn-primary bg-amber-600 hover:bg-amber-700">
                Call (705) 503-4966
              </a>
              <Link
                to="/cake-request"
                className="btn-outline border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
              >
                Cake request form
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cart Sidebar (hidden in resume-payment flow) */}
      {!payToken && isCartOpen && (
        <div className="fixed inset-0 z-[100] overflow-hidden">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseCart} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-display font-bold text-gray-900">
                  {checkoutStep === 'cart' && 'Your Cart'}
                  {checkoutStep === 'payment' && 'Payment'}
                  {checkoutStep === 'confirmation' && 'Order Confirmed!'}
                </h2>
                <button
                  onClick={handleCloseCart}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* STEP: Confirmation */}
              {checkoutStep === 'confirmation' && (
                <div className="text-center py-8">
                  {hasCakeItems ? (
                    // Cake order - awaiting approval
                    <>
                      <ClockIcon className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Order Submitted!</h3>
                      <p className="text-gray-600 mb-4">
                        Your order <span className="font-semibold">#{orderNumber}</span> is awaiting approval.
                      </p>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-6">
                        <h4 className="font-semibold text-amber-800 mb-2">🎂 Custom Cake Order</h4>
                        <p className="text-sm text-amber-700 mb-2">
                          We'll review your cake details and contact you within <strong>24 hours</strong> to confirm:
                        </p>
                        <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                          <li>Design feasibility</li>
                          <li>Final pricing</li>
                          <li>Pickup date/time</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Requested Pickup:</strong> {customerInfo.pickupDate} {customerInfo.pickupTime && `at ${customerInfo.pickupTime}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>We'll contact:</strong> {customerInfo.phone}
                        </p>
                      </div>
                      <p className="text-sm text-gray-500 mb-6">
                        Payment will be collected after your order is approved.
                      </p>
                    </>
                  ) : (
                    // Regular order - confirmed
                    <>
                      <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                      <p className="text-gray-600 mb-4">
                        Your order <span className="font-semibold">#{orderNumber}</span> has been placed.
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                        <p className="text-sm text-gray-600 mb-1">
                          <strong>Pickup:</strong> {customerInfo.pickupDate} at {customerInfo.pickupTime || 'TBD'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Payment:</strong> Paid online
                        </p>
                      </div>
                      <p className="text-sm text-gray-500 mb-6">
                        You&apos;ll receive a confirmation email shortly.
                      </p>
                    </>
                  )}
                  <button
                    onClick={handleCloseCart}
                    className="btn-primary w-full"
                  >
                    Continue Shopping
                  </button>
                </div>
              )}

              {/* STEP: Payment */}
              {checkoutStep === 'payment' && (
                <div>
                  <p className="text-gray-600 mb-4">
                    Order #{orderNumber} ready for payment.
                  </p>
                  
                  {/* Order Summary */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    {tax > 0 && (
                      <div className="flex justify-between text-sm mb-1 text-gray-600">
                        <span>Taxable amount (before HST):</span>
                        <span>${taxableSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mb-2">
                      <span>{taxLabel}{taxNote}</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{taxRuleNote}</p>
                    {sixPlusMessage && (
                      <p className="text-xs text-primary-600 mb-2">{sixPlusMessage}</p>
                    )}
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total:</span>
                      <span className="text-primary-600">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Payment Error + fallback to call and confirm order */}
                  {paymentError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-red-700 text-sm">{paymentError}</p>
                      <p className="text-gray-700 text-sm mt-2">
                        Having trouble? Call us to confirm your order
                        {bakeryPhone ? (
                          <>: <a href={`tel:${bakeryPhone.replace(/\D/g, '')}`} className="font-medium text-primary-600 hover:underline">{bakeryPhone}</a></>
                        ) : (
                          ' (see our contact page).'
                        )}
                      </p>
                    </div>
                  )}

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
                    <p className="font-medium">One more step — pay now to confirm your order.</p>
                    <p className="mt-1 text-amber-800">
                      Your cart is reserved as order #{orderNumber || '…'}, but it is not placed until payment
                      completes. You will not get a confirmation email until then.
                    </p>
                  </div>

                  {/* Square Payment Form — amount must match bakery_orders.total from the server */}
                  <SquarePaymentForm
                    amount={paymentTotal ?? total}
                    orderId={orderId || undefined}
                    customerEmail={customerInfo.email}
                    customerName={customerInfo.name}
                    campaignId={campaignCheckout?.id}
                    campaignSlug={campaignCheckout?.slug}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    onCancel={() => setCheckoutStep('cart')}
                  />
                  {paymentError && shouldOfferSquareHostedCheckout(paymentError) && orderId && (
                    <div className="mt-4">
                      <button
                        type="button"
                        disabled={hostedCheckoutLoading}
                        onClick={() => startHostedCheckout(orderId)}
                        className="w-full text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 rounded-lg px-4 py-2 disabled:opacity-60"
                      >
                        {hostedCheckoutLoading
                          ? 'Opening secure Square checkout…'
                          : 'Continue on Square’s secure checkout page'}
                      </button>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Use this if the card form fails (browser extensions or bank verification).
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* STEP: Cart */}
              {checkoutStep === 'cart' && (
                <>
                  {cartItems.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">Your cart is empty</p>
                      <Link
                        to="/menu"
                        className="inline-flex items-center gap-2 btn-primary text-sm"
                      >
                        <ShoppingCartIcon className="h-4 w-4" />
                        Browse Menu
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Browse Menu Link */}
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <Link
                          to="/menu"
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
                        >
                          <ShoppingCartIcon className="h-4 w-4" />
                          Add More Items from Menu
                        </Link>
                      </div>
                      {/* Cart Items */}
                      <div className="space-y-4 mb-6">
                        {cartItems.map((item) => (
                          <div key={item.lineId} className={`flex items-center gap-4 p-3 rounded-lg ${item.requiresApproval ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                            {item.image && (
                              <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                            )}
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">
                                {item.name}
                                {item.requiresApproval && (
                                  <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                                    Custom
                                  </span>
                                )}
                              </h4>
                              {item.notes && (
                                <p className="text-xs text-gray-600 mt-1">{item.notes}</p>
                              )}
                              {isStuffedCookie(item) && (
                                <button
                                  type="button"
                                  onClick={() => setStuffedCookieEditItem(item)}
                                  className="text-xs text-primary-600 hover:underline mt-1"
                                >
                                  Change flavours
                                </button>
                              )}
                              {isButterTart(item) && (
                                <button
                                  type="button"
                                  onClick={() => setButterTartEditItem(item)}
                                  className="text-xs text-primary-600 hover:underline mt-1"
                                >
                                  Change flavour
                                </button>
                              )}
                              <p className="text-sm text-gray-600">
                                ${item.price.toFixed(2)} {item.requiresApproval ? '(starting)' : 'each'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                                className="p-1 hover:bg-gray-200 rounded"
                                aria-label="Decrease quantity"
                                title="Decrease quantity"
                              >
                                <MinusIcon className="h-4 w-4" />
                              </button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                                className="p-1 hover:bg-gray-200 rounded"
                                aria-label="Increase quantity"
                                title="Increase quantity"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => removeItem(item.lineId)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded ml-2"
                                aria-label="Remove item"
                                title="Remove item"
                                data-testid="remove-cart-item"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Cake Details Form - Show when cart has cake items */}
                      {hasCakeItems && (
                        <div className="mb-6">
                          <CakeDetailsForm 
                            initialDetails={cakeDetails || undefined}
                            onChange={handleCakeDetailsChange}
                            compact={true}
                          />
                        </div>
                      )}

                      {/* Helpful Guides (internal linking) - show for custom cake orders */}
                      {hasCakeItems && (
                        <HelpfulGuides
                          className="mb-8"
                          title="Helpful Guides for Custom Cakes"
                          limit={3}
                        />
                      )}

                      {/* Order Total */}
                      <div className="border-t pt-4 mb-6">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Subtotal:</span>
                          <span>${subtotal.toFixed(2)}</span>
                        </div>
                        {tax > 0 && (
                          <div className="flex justify-between text-sm mb-1 text-gray-600">
                            <span>Taxable amount (before HST):</span>
                            <span>${taxableSubtotal.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">{taxLabel}{taxNote}</span>
                          <span>${tax.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{taxRuleNote}</p>
                        {sixPlusMessage && (
                          <p className="text-xs text-primary-600 mb-2">{sixPlusMessage}</p>
                        )}
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total:</span>
                          <span className="text-primary-600">${total.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Validation Error Message */}
                      {validationError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-700 text-sm">{validationError}</p>
                        </div>
                      )}

                      {/* Customer Info */}
                      <div className="space-y-4 mb-6">
                        <h3 className="font-semibold text-gray-900">Pickup Information</h3>
                        <input
                          type="text"
                          placeholder="Your Name *"
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number *"
                          value={customerInfo.phone}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                        <input
                          type="email"
                          placeholder="Email (for receipt & confirmation) *"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                        <p className="text-xs text-gray-500 -mt-2">We need your email to send order confirmation and receipt.</p>
                        <label htmlFor="pickup-date" className="block text-sm font-medium text-gray-700 mt-2">Pickup date *</label>
                        <p className="text-xs text-gray-500 mb-1">
                          You can order online anytime. Pickup must be on a day we&apos;re open — choose your date below.
                        </p>
                        <select
                          id="pickup-date"
                          aria-label="Pickup date"
                          value={customerInfo.pickupDate}
                          onChange={(e) => {
                            const date = e.target.value;
                            setCustomerInfo({ ...customerInfo, pickupDate: date, pickupTime: '' });
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                          disabled={visiblePickupDates.length === 0}
                        >
                          <option value="">Select pickup date</option>
                          {visiblePickupDates.map((date) => (
                            <option key={date} value={date}>
                              {formatCalendarDateEnCA(date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            </option>
                          ))}
                        </select>
                        {pickupCap && (
                          <p className="text-sm text-gray-600 mt-1">
                            Seasonal items in your cart must be picked up by {formatCalendarDateEnCA(pickupCap, { weekday: 'long', month: 'long', day: 'numeric' })}, during open hours.
                          </p>
                        )}
                        {closedPreorderNames.length > 0 && (
                          <p className="text-sm text-amber-700 mt-1" role="status">
                            Orders have closed for {closedPreorderNames.join(', ')}.
                          </p>
                        )}
                        {visiblePickupDates.length === 0 && businessHours.length > 0 && (
                          <p className="text-sm text-amber-700 mt-1" role="status">
                            No pickup dates available right now. Please check back or call us.
                          </p>
                        )}
                        {customerInfo.pickupDate && !isPickupDayClosed && (
                          <p className="text-sm text-green-700 mt-1" role="status">
                            ✓ Pickup date: {new Date(customerInfo.pickupDate + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        {customerInfo.pickupDate && isPickupDayClosed && (
                          <p className="text-sm text-amber-700" role="status">
                            {isClosureDate(customerInfo.pickupDate)
                              ? "We're closed on this date (holiday). Your order is welcome — please pick another pickup date when we're open."
                              : `We're closed on ${getDayInfoForDate(customerInfo.pickupDate)?.day ?? 'this day'}. Your order is welcome — please pick another pickup date when we're open.`}
                          </p>
                        )}
                        <select
                          value={customerInfo.pickupTime}
                          aria-label="Pickup time"
                          title="Select pickup time"
                          onChange={(e) => setCustomerInfo({ ...customerInfo, pickupTime: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!customerInfo.pickupDate || isPickupDayClosed}
                        >
                          <option value="">Select Pickup Time</option>
                          {pickupTimeOptions.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        {customerInfo.pickupDate && !isPickupDayClosed && pickupTimeOptions.length === 0 && businessHours.length > 0 && (
                          <p className="text-xs text-gray-500">No times available for this day.</p>
                        )}
                        <textarea
                          placeholder="Special requests or notes"
                          value={customerInfo.notes}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={2}
                        />
                      </div>

                      {/* Checkout: online payment only; no Pay at Pickup option */}
                      <button
                        onClick={handleCheckout}
                        disabled={isCheckingOut || isPickupDayClosed}
                        className="w-full btn-primary py-4 text-lg disabled:opacity-50"
                      >
                        {isCheckingOut
                          ? 'Creating your order — please wait…'
                          : `Continue to Payment - $${total.toFixed(2)}`}
                      </button>

                      <p className="text-xs text-gray-500 text-center mt-4">
                        You will be prompted to enter your card details on the next step.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit stuffed cookie flavours (from cart) */}
      {stuffedCookieEditItem && (
        <StuffedCookieSelectionModal
          isOpen={true}
          isEditMode={true}
          onClose={() => setStuffedCookieEditItem(null)}
          onConfirm={(selection) => {
            if (stuffedCookieEditItem.lineId) {
              updateItemNotes(stuffedCookieEditItem.lineId, selection);
            } else {
              setValidationError('Could not update flavours. Try removing the item and re-adding it from the menu.');
            }
            setStuffedCookieEditItem(null);
          }}
          productId={stuffedCookieEditItem.id}
          productName={stuffedCookieEditItem.name}
          productPrice={stuffedCookieEditItem.price}
          initialNotes={stuffedCookieEditItem.notes ?? undefined}
        />
      )}

      {/* Edit butter tart flavour (from cart) */}
      {butterTartEditItem && (
        <ButterTartSelectionModal
          isOpen={true}
          isEditMode={true}
          onClose={() => setButterTartEditItem(null)}
          onConfirm={(selection) => {
            if (butterTartEditItem.lineId) {
              updateItemNotes(butterTartEditItem.lineId, selection);
            } else {
              setValidationError('Could not update flavour. Try removing the item and re-adding it from the menu.');
            }
            setButterTartEditItem(null);
          }}
          productId={butterTartEditItem.id}
          productName={butterTartEditItem.name}
          productPrice={butterTartEditItem.price}
          initialNotes={butterTartEditItem.notes ?? undefined}
        />
      )}
    </div>
  );
}

